import { useOutsideAlerter } from '@hooks';
import { type Placement } from '@popperjs/core';
import clsx from 'clsx';
import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

import styles from './DeletePopup.module.scss';

type DeletePopupProps = {
  show: boolean;
  anchorRef: React.RefObject<HTMLElement>;
  className?: string;
  placement?: Placement;
  onClose: () => void;
  onDelete: () => void;
};

export const DeletePopup: React.FC<DeletePopupProps> = ({
  className,
  placement = 'top-end',
  show,
  anchorRef,
  onDelete,
  onClose,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);

  const setRefs = useCallback((el: HTMLDivElement | null) => {
    popupRef.current = el;
    setPopperElement(el);
  }, []);

  const { styles: popperStyles, attributes } = usePopper(anchorRef.current, popperElement, {
    placement,
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      { name: 'preventOverflow', options: { padding: 8 } },
    ],
  });

  const handleOutsideClick = useCallback(() => onClose(), [onClose]);
  useOutsideAlerter(popupRef, handleOutsideClick);

  if (!show) return null;

  return createPortal(
    <div
      ref={setRefs}
      data-delete-popup
      className={clsx(styles.popup, className)}
      style={popperStyles.popper}
      {...attributes.popper}
    >
      <div className={styles.info}>Are you sure you want to delete this?</div>
      <div className={styles.controls}>
        <div onClick={onClose}>Close</div>
        <div
          onClick={() => {
            onClose();
            onDelete();
          }}
        >
          Delete
        </div>
      </div>
    </div>,
    document.body,
  );
};
